import React, {ReactNode, useEffect, useMemo, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import {FaCarSide} from 'react-icons/fa'
import {convertToHours, formatDateToDDMMYYYY} from '../../common-function/formatDateToYearMonth'
import {BiSolidUser} from 'react-icons/bi'
import {KTIcon} from '../../../_metronic/helpers'
import {resolveMediaUrl} from '../../../api-utils/constantVariables'

type DetailItem = {
  label: string
  value: ReactNode
}

const EMPTY_VALUE = '—'

const formatDate = (value?: string | Date | null) => {
  if (!value) return EMPTY_VALUE
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  return formatDateToDDMMYYYY(date)
}

const formatTime = (value?: number | string | null) => {
  const timestamp = Number(value)
  if (!Number.isFinite(timestamp) || timestamp <= 0) return EMPTY_VALUE
  return convertToHours(timestamp)
}

const formatDateTime = (value?: string | Date | number | null) => {
  if (!value) return EMPTY_VALUE
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE
  return `${formatDateToDDMMYYYY(date)} ${convertToHours(date.getTime())}`
}

const formatDistance = (value?: number | string | null) => {
  const distance = Number(value)
  if (!Number.isFinite(distance) || distance <= 0) return EMPTY_VALUE
  if (distance >= 1000) return `${(distance / 1000).toFixed(1)} km`
  return `${Math.round(distance)} m`
}

const formatDuration = (value?: number | string | null) => {
  const seconds = Number(value)
  if (!Number.isFinite(seconds) || seconds <= 0) return EMPTY_VALUE
  const minutes = seconds > 1000 * 60 ? Math.round(seconds / 60000) : Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`
}

const formatMoney = (value?: number | string | null) => {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return EMPTY_VALUE
  return `$${amount.toFixed(2)}`
}

const formatRole = (trip: any) => {
  if (trip?.trip_created_by === 2) return 'Driver trip'
  if (trip?.trip_created_by === 3) return 'Passenger trip'
  return 'Trip'
}

const formatRequestedBy = (value?: number | string | null) => {
  if (Number(value) === 2) return 'Driver'
  if (Number(value) === 3) return 'Passenger'
  return EMPTY_VALUE
}

const formatYesNo = (value?: boolean | null) => (value == null ? EMPTY_VALUE : value ? 'Yes' : 'No')

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'completed':
      return 'badge-light-success'
    case 'cancel':
    case 'driver_not_show':
    case 'driver_move_on':
    case 'unsuccessful':
    case 'deactivate':
      return 'badge-light-danger'
    case 'not_started':
      return 'badge-light-warning'
    case 'in_progress':
      return 'badge-light-primary'
    default:
      return 'badge-light-secondary'
  }
}

const DetailRow = ({label, value}: DetailItem) => (
  <div className='d-flex justify-content-between gap-4 py-3 border-bottom border-gray-200'>
    <span className='text-muted fw-semibold'>{label}</span>
    <span className='fw-semibold text-gray-900 text-end text-break'>{value || EMPTY_VALUE}</span>
  </div>
)

const InfoCard = ({title, items}: {title: string; items: DetailItem[]}) => (
  <div className='card h-100'>
    <div className='card-header border-0 pt-6'>
      <h3 className='card-title fw-bold text-gray-800'>{title}</h3>
    </div>
    <div className='card-body pt-0'>
      {items.map((item) => (
        <DetailRow key={item.label} {...item} />
      ))}
    </div>
  </div>
)

const WalletLedger = ({entries}: {entries: any[]}) => {
  if (!entries?.length) return null

  return (
    <div className='card mb-6'>
      <div className='card-header border-0 pt-6'>
        <div>
          <h3 className='card-title fw-bold text-gray-800 mb-1'>Wallet / points ledger</h3>
          <div className='text-muted fs-7'>Credits and debits linked to this trip</div>
        </div>
      </div>
      <div className='card-body pt-0'>
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bold fs-7 text-uppercase'>
                <th>User</th>
                <th>Type</th>
                <th>Points</th>
                <th>Amount</th>
                <th>Trip</th>
                <th>Payment</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody className='text-gray-700 fw-semibold'>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.user?.id ? (
                      <Link
                        to={`/user-management/${entry.user.id}`}
                        className='text-gray-900 text-hover-primary'
                      >
                        {entry.user?.name || `User #${entry.user.id}`}
                      </Link>
                    ) : (
                      entry.user_name || EMPTY_VALUE
                    )}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        entry.type === 'credit' ? 'badge-light-success' : 'badge-light-danger'
                      }`}
                    >
                      {entry.transaction_type ?? entry.type ?? EMPTY_VALUE}
                    </span>
                  </td>
                  <td>{entry.points ?? EMPTY_VALUE}</td>
                  <td>{entry.amount ?? EMPTY_VALUE}</td>
                  <td>
                    {entry.trip_id ? (
                      <Link
                        to={`/trip-management/${entry.trip_id}`}
                        className='text-gray-900 text-hover-primary'
                      >
                        {entry.trip_id}
                      </Link>
                    ) : (
                      EMPTY_VALUE
                    )}
                  </td>
                  <td>
                    {entry.payment_id ? (
                      <Link
                        to={`/payment/${entry.payment_id}`}
                        className='text-gray-900 text-hover-primary'
                      >
                        {entry.payment?.transaction_id || entry.payment_id}
                      </Link>
                    ) : (
                      EMPTY_VALUE
                    )}
                  </td>
                  <td>{formatDateTime(entry.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

const UserAvatar = ({user, size = '75px'}: {user?: any; size?: string}) => {
  const src = resolveMediaUrl(user?.profile_picture)

  return (
    <div
      className='symbol symbol-circle bg-light-primary flex-shrink-0 d-flex align-items-center justify-content-center overflow-hidden'
      style={{width: size, height: size}}
    >
      {src ? (
        <img
          src={src}
          alt={user?.name || 'User'}
          className='w-100 h-100'
          style={{objectFit: 'cover'}}
        />
      ) : (
        <span className='symbol-label w-100 h-100 d-flex align-items-center justify-content-center bg-light-primary'>
          <BiSolidUser className='text-primary' style={{fontSize: '2.75rem'}} />
        </span>
      )}
    </div>
  )
}

const TripDetails = () => {
  const params = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [tripData, setTripData] = useState<any>(null)
  const [error, setError] = useState('')

  const fetchTripData = async () => {
    if (!params.id) return
    setIsLoading(true)
    setError('')
    try {
      const response = await restRequest.getTripDetailData(params.id)
      const payload = response?.data?.data
      if (payload) {
        setTripData(payload)
      } else {
        setTripData(null)
        setError(response?.data?.message || 'Trip not found.')
      }
    } catch (err: any) {
      setTripData(null)
      setError(err?.response?.data?.message || err?.message || 'Unable to load trip details.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTripData()
  }, [params.id])

  const passengerTrips = useMemo(() => tripData?.carpoolers ?? [], [tripData])

  const scheduleItems: DetailItem[] = useMemo(
    () => [
      {label: 'Start date', value: formatDate(tripData?.start_date)},
      {label: 'Desired pickup time', value: formatTime(tripData?.start_desired_time)},
      {label: 'Latest pickup time', value: formatTime(tripData?.start_latest_time)},
      {label: 'Expected end time', value: formatTime(tripData?.expected_trip_end_time)},
      {label: 'Actual start time', value: formatTime(tripData?.actual_trip_start_time)},
      {label: 'Actual end time', value: formatTime(tripData?.actual_trip_end_time)},
      {label: 'Cancelled at', value: formatDateTime(tripData?.cancel_at)},
    ],
    [tripData]
  )

  const tripItems: DetailItem[] = useMemo(
    () => [
      {label: 'Trip ID', value: tripData?.id ?? EMPTY_VALUE},
      {label: 'Trip type', value: tripData?.trip_type ?? EMPTY_VALUE},
      {label: 'Created as', value: formatRole(tripData)},
      {label: 'Seats', value: `${tripData?.booked_seat ?? 0} / ${tripData?.no_of_seats ?? 0}`},
      {label: 'Repeat weekly', value: tripData?.repeat_week ? 'Yes' : 'No'},
      {
        label: 'Days of week',
        value: tripData?.days_of_week?.length ? tripData.days_of_week.join(', ') : EMPTY_VALUE,
      },
      {label: 'Return trip', value: tripData?.is_return ? 'Yes' : 'No'},
      {label: 'Return date', value: formatDate(tripData?.return_date)},
      {
        label: 'Return trip ID',
        value: tripData?.return_trip_id ? (
          <Link
            to={`/trip-management/${tripData.return_trip_id}`}
            className='text-gray-900 text-hover-primary'
          >
            {tripData.return_trip_id}
          </Link>
        ) : (
          EMPTY_VALUE
        ),
      },
      {
        label: 'Linked driver trip ID',
        value: tripData?.driver_trip_id ? (
          <Link
            to={`/trip-management/${tripData.driver_trip_id}`}
            className='text-gray-900 text-hover-primary'
          >
            {tripData.driver_trip_id}
          </Link>
        ) : (
          EMPTY_VALUE
        ),
      },
    ],
    [tripData]
  )

  const fareItems: DetailItem[] = useMemo(
    () => [
      {label: 'Estimate', value: formatMoney(tripData?.estimate)},
      {label: 'Paid points', value: tripData?.paid_points ?? EMPTY_VALUE},
      {label: 'Trip points', value: tripData?.points ?? EMPTY_VALUE},
      {label: 'Distance', value: formatDistance(tripData?.distance)},
      {label: 'Trip duration', value: formatDuration(tripData?.trip_time)},
      {label: 'Created at', value: formatDateTime(tripData?.createdAt)},
      {label: 'Updated at', value: formatDateTime(tripData?.updatedAt)},
    ],
    [tripData]
  )

  const operationalItems: DetailItem[] = useMemo(
    () => [
      {label: 'Drop request time', value: formatDateTime(tripData?.drop_request_time)},
      {label: 'Drop alert sent', value: formatYesNo(tripData?.drop_alert_sent)},
      {label: 'Deleted at', value: formatDateTime(tripData?.deleted_at)},
      {
        label: 'Actual pickup GPS',
        value: tripData?.start_lat_lng
          ? `${tripData.start_lat_lng.lat ?? EMPTY_VALUE}, ${
              tripData.start_lat_lng.lng ?? EMPTY_VALUE
            }`
          : EMPTY_VALUE,
      },
      {
        label: 'Actual drop-off GPS',
        value: tripData?.end_lat_lng
          ? `${tripData.end_lat_lng.lat ?? EMPTY_VALUE}, ${
              tripData.end_lat_lng.lng ?? EMPTY_VALUE
            }`
          : EMPTY_VALUE,
      },
    ],
    [tripData]
  )

  const user = tripData?.user
  const driver = tripData?.driver ?? (tripData?.trip_created_by === 2 ? tripData?.user : null)
  const driverCar = tripData?.driver_car
  const primaryRequest = tripData?.request
  const walletEntries = tripData?.wallet_entries ?? []

  const driverItems: DetailItem[] = [
    {
      label: 'Driver',
      value: driver?.id ? (
        <Link to={`/user-management/${driver.id}`} className='text-gray-900 text-hover-primary'>
          {driver.name || `User #${driver.id}`}
        </Link>
      ) : (
        EMPTY_VALUE
      ),
    },
    {label: 'Driver ID', value: driver?.id ?? EMPTY_VALUE},
    {label: 'Email', value: driver?.email ?? EMPTY_VALUE},
    {label: 'Mobile', value: driver?.mobile ?? EMPTY_VALUE},
    {label: 'Rating', value: driver?.avg_rating ?? EMPTY_VALUE},
  ]

  const vehicleItems: DetailItem[] = [
    {label: 'Brand', value: driverCar?.car_brand_name ?? EMPTY_VALUE},
    {label: 'Model', value: driverCar?.car_model_name ?? EMPTY_VALUE},
    {label: 'Registration', value: driverCar?.car_registration ?? EMPTY_VALUE},
    {label: 'Color', value: driverCar?.color ?? EMPTY_VALUE},
    {label: 'Seats', value: driverCar?.no_of_seats ?? EMPTY_VALUE},
  ]

  const requestItems: DetailItem[] = [
    {label: 'Request ID', value: primaryRequest?.id ?? EMPTY_VALUE},
    {label: 'Linked requests', value: tripData?.requests?.length ?? 0},
    {label: 'Request status', value: primaryRequest?.status ?? EMPTY_VALUE},
    {label: 'Requested by', value: formatRequestedBy(primaryRequest?.requested_by)},
    {label: 'Driver trip ID', value: primaryRequest?.driver_trip_id ?? EMPTY_VALUE},
    {label: 'Passenger trip ID', value: primaryRequest?.passenger_trip_id ?? EMPTY_VALUE},
    {label: 'Accepted on', value: formatDateTime(primaryRequest?.accepted_on)},
    {label: 'Last request update', value: formatDateTime(primaryRequest?.status_updated_on)},
  ]

  return (
    <div className='w-100'>
      <div className='d-flex align-items-center gap-3 mb-5'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => navigate('/trip-management')}
        >
          ← Back to Trip Management
        </button>
      </div>

      {isLoading ? (
        <div className='d-flex justify-content-center mt-10'>
          <div className='spinner-border text-primary' role='status' />
        </div>
      ) : error || !tripData ? (
        <div className='alert alert-danger'>{error || 'Trip not found.'}</div>
      ) : (
        <div>
          <div className='card mb-6'>
            <div className='card-body p-9'>
              <div className='d-flex flex-column flex-xl-row justify-content-between gap-8'>
                <div className='d-flex align-items-center gap-6'>
                  <UserAvatar user={user} size='88px' />
                  <div>
                    <div className='d-flex align-items-center gap-3 flex-wrap mb-2'>
                      <h1 className='fs-2x fw-bold text-gray-900 mb-0'>Trip #{tripData.id}</h1>
                      <span className={`badge ${statusBadgeClass(tripData.trip_status)}`}>
                        {tripData.trip_status ?? EMPTY_VALUE}
                      </span>
                      <span className='badge badge-light-info'>{formatRole(tripData)}</span>
                    </div>
                    {user?.id ? (
                      <Link
                        to={`/user-management/${user.id}`}
                        className='fs-4 fw-semibold text-gray-800 text-hover-primary'
                      >
                        {user.name || 'View user'}
                      </Link>
                    ) : (
                      <div className='fs-4 fw-semibold text-gray-800'>
                        {user?.name ?? EMPTY_VALUE}
                      </div>
                    )}
                    <div className='text-muted mt-1'>{user?.email ?? EMPTY_VALUE}</div>
                    <div className='text-muted mt-1'>{user?.mobile ?? EMPTY_VALUE}</div>
                  </div>
                </div>

                <div className='row g-4 min-w-xl-450px'>
                  <div className='col-6'>
                    <div className='border border-dashed border-gray-300 rounded px-5 py-4 h-100'>
                      <div className='text-muted fw-semibold fs-7'>Seats booked</div>
                      <div className='fs-2 fw-bold text-gray-900'>
                        {tripData.booked_seat ?? 0}/{tripData.no_of_seats ?? 0}
                      </div>
                    </div>
                  </div>
                  <div className='col-6'>
                    <div className='border border-dashed border-gray-300 rounded px-5 py-4 h-100'>
                      <div className='text-muted fw-semibold fs-7'>Paid points</div>
                      <div className='fs-2 fw-bold text-gray-900'>
                        {tripData.paid_points ?? EMPTY_VALUE}
                      </div>
                    </div>
                  </div>
                  <div className='col-6'>
                    <div className='border border-dashed border-gray-300 rounded px-5 py-4 h-100'>
                      <div className='text-muted fw-semibold fs-7'>Distance</div>
                      <div className='fs-3 fw-bold text-gray-900'>
                        {formatDistance(tripData.distance)}
                      </div>
                    </div>
                  </div>
                  <div className='col-6'>
                    <div className='border border-dashed border-gray-300 rounded px-5 py-4 h-100'>
                      <div className='text-muted fw-semibold fs-7'>Estimate</div>
                      <div className='fs-3 fw-bold text-gray-900'>
                        {formatMoney(tripData.estimate)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(tripData.cancel_reason || tripData.note) && (
            <div className='alert alert-dismissible bg-light-danger d-flex flex-column flex-sm-row p-5 mb-6'>
              <KTIcon iconName='information-5' className='fs-2hx text-danger me-4 mb-5 mb-sm-0' />
              <div className='d-flex flex-column text-danger'>
                <h4 className='mb-1 text-danger'>Trip note</h4>
                <span>{tripData.cancel_reason || tripData.note}</span>
              </div>
            </div>
          )}

          <div className='card mb-6'>
            <div className='card-body p-9'>
              <div className='row g-8 align-items-stretch'>
                <div className='col-lg-5'>
                  <div className='bg-light rounded p-6 h-100'>
                    <div className='d-flex align-items-start gap-4 mb-8'>
                      <span className='bullet bullet-dot bg-success h-12px w-12px mt-2'></span>
                      <div>
                        <div className='text-muted fw-semibold mb-1'>Pickup</div>
                        <div className='fw-bold text-gray-900 text-break'>
                          {tripData.start_address || EMPTY_VALUE}
                        </div>
                        <div className='text-muted fs-7 mt-1'>
                          {tripData.start_lat ?? EMPTY_VALUE}, {tripData.start_long ?? EMPTY_VALUE}
                        </div>
                      </div>
                    </div>
                    <div className='border-start border-2 border-dashed border-gray-400 ms-1 h-40px'></div>
                    <div className='d-flex align-items-start gap-4 mt-8'>
                      <span className='bullet bullet-dot bg-danger h-12px w-12px mt-2'></span>
                      <div>
                        <div className='text-muted fw-semibold mb-1'>Drop-off</div>
                        <div className='fw-bold text-gray-900 text-break'>
                          {tripData.end_address || EMPTY_VALUE}
                        </div>
                        <div className='text-muted fs-7 mt-1'>
                          {tripData.end_lat ?? EMPTY_VALUE}, {tripData.end_long ?? EMPTY_VALUE}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='col-lg-7'>
                  <div className='d-flex flex-column justify-content-center h-100'>
                    <div className='d-flex align-items-center gap-4 mb-5'>
                      <FaCarSide className='text-primary fs-1' />
                      <div>
                        <h3 className='fw-bold text-gray-900 mb-1'>Route overview</h3>
                        <div className='text-muted'>
                          {formatDate(tripData.start_date)} · {formatTime(tripData.start_desired_time)}
                        </div>
                      </div>
                    </div>
                    <div className='row g-4'>
                      <div className='col-md-4'>
                        <div className='bg-light-primary rounded p-5 h-100'>
                          <div className='text-muted fw-semibold fs-7'>Trip status</div>
                          <div className='fw-bold text-gray-900 mt-1'>
                            {tripData.trip_status ?? EMPTY_VALUE}
                          </div>
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='bg-light-primary rounded p-5 h-100'>
                          <div className='text-muted fw-semibold fs-7'>Actual start</div>
                          <div className='fw-bold text-gray-900 mt-1'>
                            {formatTime(tripData.actual_trip_start_time)}
                          </div>
                        </div>
                      </div>
                      <div className='col-md-4'>
                        <div className='bg-light-primary rounded p-5 h-100'>
                          <div className='text-muted fw-semibold fs-7'>Actual end</div>
                          <div className='fw-bold text-gray-900 mt-1'>
                            {formatTime(tripData.actual_trip_end_time)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='row g-6 mb-6'>
            <div className='col-xl-4'>
              <InfoCard title='Schedule' items={scheduleItems} />
            </div>
            <div className='col-xl-4'>
              <InfoCard title='Trip configuration' items={tripItems} />
            </div>
            <div className='col-xl-4'>
              <InfoCard title='Fare & metadata' items={fareItems} />
            </div>
            <div className='col-xl-4'>
              <InfoCard title='Operational flags' items={operationalItems} />
            </div>
          </div>

          <div className='row g-6 mb-6'>
            <div className='col-xl-4'>
              <InfoCard title='Driver details' items={driverItems} />
            </div>
            <div className='col-xl-4'>
              <InfoCard title='Vehicle details' items={vehicleItems} />
            </div>
            <div className='col-xl-4'>
              <InfoCard title='Request details' items={requestItems} />
            </div>
          </div>

          <WalletLedger entries={walletEntries} />

          {tripData.trip_created_by === 2 && (
            <div className='card mb-6'>
              <div className='card-header border-0 pt-6'>
                <div>
                  <h3 className='card-title fw-bold text-gray-800 mb-1'>Passengers</h3>
                  <div className='text-muted fs-7'>
                    {passengerTrips.length} linked passenger trip
                    {passengerTrips.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
              <div className='card-body pt-0'>
                {passengerTrips.length > 0 ? (
                  <div className='row g-6'>
                    {passengerTrips.map((item: any) => (
                      <div className='col-xl-6' key={item.id}>
                        <div className='border border-gray-300 rounded p-6 h-100'>
                          <div className='d-flex align-items-start justify-content-between gap-4 mb-5'>
                            <div className='d-flex align-items-center gap-4'>
                              <UserAvatar user={item.user} size='56px' />
                              <div>
                                {item.user?.id ? (
                                  <Link
                                    to={`/user-management/${item.user.id}`}
                                    className='fw-bold fs-5 text-gray-900 text-hover-primary'
                                  >
                                    {item.user?.name || 'View user'}
                                  </Link>
                                ) : (
                                  <div className='fw-bold fs-5 text-gray-900'>
                                    {item.user?.name ?? EMPTY_VALUE}
                                  </div>
                                )}
                                <div className='text-muted fs-7'>{item.user?.email ?? EMPTY_VALUE}</div>
                                <div className='text-muted fs-7'>{item.user?.mobile ?? EMPTY_VALUE}</div>
                              </div>
                            </div>
                            <span className={`badge ${statusBadgeClass(item.trip_status)}`}>
                              {item.trip_status ?? EMPTY_VALUE}
                            </span>
                          </div>

                          <div className='row g-4 mb-5'>
                            <div className='col-md-6'>
                              <div className='text-muted fs-7'>Pickup</div>
                              <div className='fw-semibold text-gray-900 text-break'>
                                {item.start_address || EMPTY_VALUE}
                              </div>
                            </div>
                            <div className='col-md-6'>
                              <div className='text-muted fs-7'>Drop-off</div>
                              <div className='fw-semibold text-gray-900 text-break'>
                                {item.end_address || EMPTY_VALUE}
                              </div>
                            </div>
                          </div>

                          <div className='row g-4'>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Date</div>
                                <div className='fw-bold'>{formatDate(item.start_date)}</div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Desired time</div>
                                <div className='fw-bold'>{formatTime(item.start_desired_time)}</div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Paid points</div>
                                <div className='fw-bold'>{item.paid_points ?? EMPTY_VALUE}</div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Quoted points</div>
                                <div className='fw-bold'>{item.points ?? EMPTY_VALUE}</div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Passenger trip ID</div>
                                <div className='fw-bold'>
                                  {item.id ? (
                                    <Link
                                      to={`/trip-management/${item.id}`}
                                      className='text-gray-900 text-hover-primary'
                                    >
                                      {item.id}
                                    </Link>
                                  ) : (
                                    EMPTY_VALUE
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Request status</div>
                                <div className='fw-bold'>
                                  {item.request?.status ?? EMPTY_VALUE}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Accepted on</div>
                                <div className='fw-bold'>
                                  {formatDateTime(item.request?.accepted_on)}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Actual start</div>
                                <div className='fw-bold'>
                                  {formatTime(item.actual_trip_start_time)}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Actual end</div>
                                <div className='fw-bold'>
                                  {formatTime(item.actual_trip_end_time)}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Drop request</div>
                                <div className='fw-bold'>
                                  {formatDateTime(item.drop_request_time)}
                                </div>
                              </div>
                            </div>
                            <div className='col-6'>
                              <div className='bg-light rounded p-4'>
                                <div className='text-muted fs-7'>Drop alert sent</div>
                                <div className='fw-bold'>
                                  {formatYesNo(item.drop_alert_sent)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {(item.cancel_reason || item.note) && (
                            <div className='notice d-flex bg-light-danger rounded border-danger border border-dashed p-4 mt-5'>
                              <div className='text-danger fw-semibold'>
                                {item.cancel_reason || item.note}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className='text-center text-muted fs-5 py-10'>Passengers are not found.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TripDetails
